import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode,
  getIdToken,
  onAuthStateChanged,
  fetchSignInMethodsForEmail,
  sendEmailVerification,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  User,
  deleteUser,
} from 'firebase/auth';

import { auth } from '../../firebase';
import { jwtDecode } from "jwt-decode";
import apiVariables from './apiVariables';
import { saveSensitiveData, removeSensitiveData } from '../../utils/secureStorage';
import PlayIntegrity from '../../plugins/playIntegrity';
import { Capacitor } from '@capacitor/core';
import { ensureAccountClaims } from './accountClaimsGate';

export interface AuthResponse {
  token: string;
  userId: string;
  email: string | null;
}
type FirebaseTokenClaims = {
  userId?: string;
  role?: string;
  planId?: number;
  client?: string;
  sub: string; 
  email?: string;
};

// export const login = async (
//   email: string,
//   password: string
// ): Promise<AuthResponse> => {
//   const userCredential = await signInWithEmailAndPassword(
//     auth,
//     email,
//     password
//   );

//   const user = userCredential.user;
//    if (!user.emailVerified) {
          
//            throw new Error("Please verify your email before login.");
     
//         }
//   const initialToken = await getIdToken(user, true);

//   await fetch(apiVariables.ATTCH_AUTH_CLAIMS_IN_TOKEN, {
//     method: "POST",
//     headers: {
//          Authorization: `Bearer ${initialToken}`,
//         },
//         credentials: "include", 
//   });



//   const token = await getIdToken(user, true);
//   const decoded = jwtDecode<FirebaseTokenClaims>(token);

// if (!decoded.userId) {
//   throw new Error("userId claim missing in token");
// }

// await saveSensitiveData("authToken", token);
// await saveSensitiveData("userId", decoded.userId);     // ✅ DB GUID

//   return {
//     token,
//     userId: decoded.userId,
//     email: user.email,
//   };
// };

export const login = async (
  email: string,
  password: string
): Promise<AuthResponse> => {

  let integrityToken: string | null = null;
  let nonce: string | null = null;
  let platform: string | null = null;

 
  if(import.meta.env.VITE_APP_ENABLE_INTEGRITY === 'true')
  {
  //  Only for Android (skip for web and production)
  //if (Capacitor.getPlatform() === 'android') {
  //if (Capacitor.getPlatform() === 'android' && import.meta.env.node_value === 'production')
    if (Capacitor.getPlatform() === 'android' && import.meta.env.VITE_NODE_ENV === 'production')
    {

    platform='android';
    // 🔹 Get nonce from backend
    const nonceRes = await fetch(apiVariables.GET_NONCE);
    nonce = await nonceRes.text();
 
    // 🔹 Get Play Integrity token
    const integrityRes = await PlayIntegrity.getIntegrityToken({ nonce });
    integrityToken = integrityRes.token;
     


  }
}

  // ✅ 2️⃣ Firebase login (same as before)
  const userCredential = await signInWithEmailAndPassword(
    auth,
    email,
    password
  );

  const user = userCredential.user;

  if (!user.emailVerified) {
    throw new Error("Please verify your email before login.");
  }

  const claimsResult = await ensureAccountClaims(user, {
    ...(integrityToken && { "X-Integrity-Token": integrityToken }),
    ...(nonce && { "X-Nonce": nonce }),
    ...(platform && { "X-Platform": platform }),
  });

  if (!claimsResult.ok) {
    throw new Error(claimsResult.message || "Something went wrong");
  }

  const token = claimsResult.token;

  const decoded = jwtDecode<FirebaseTokenClaims>(token);

  if (!decoded.userId) {
    throw new Error("userId claim missing in token");
  }

  await saveSensitiveData("authToken", token);
  await saveSensitiveData("userId", decoded.userId);

  return {
    token,
    userId: decoded.userId,
    email: user.email,
  };
};

export const register = async (
  email: string,
  password: string
): Promise<AuthResponse> => {
  const userCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password
  );

  const user = userCredential.user;
  const initialToken = await getIdToken(user, true);

  await fetch(apiVariables.REGISTER_WITH_FIREBASE, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${initialToken}`,
    },
  });

  const token = await getIdToken(user, true);

const decoded = jwtDecode<FirebaseTokenClaims>(token);

if (!decoded.userId) {
  throw new Error("userId claim missing in token");
}

await saveSensitiveData("authToken", token);
await saveSensitiveData("userId", decoded.userId);     



return {
    token,
    userId: decoded.userId,
    email: user.email,
  };
};

export const logout = async (): Promise<void> => {
  await signOut(auth);
  await removeSensitiveData('authToken');
  await removeSensitiveData('userId');
  await removeSensitiveData('selectedMatchId');
  await removeSensitiveData('selectedMatchProfile');
  await removeSensitiveData('fromUserProfile');
};

export const deleteAccount = async (): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No user is currently logged in');
  }

  await deleteUser(user);
  await removeSensitiveData('authToken');
  await removeSensitiveData('userId');
  await removeSensitiveData('selectedMatchId');
  await removeSensitiveData('selectedMatchProfile');
  await removeSensitiveData('fromUserProfile');
};

export const refreshToken = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (!user) return null;

  const token = await getIdToken(user, true);
  await saveSensitiveData('authToken', token);
  return token;
};

export const forgotPassword = async (email: string): Promise<void> => {
  await sendPasswordResetEmail(auth, email, {
    // Sends the user to our own branded page instead of Firebase's default
    // hosted action-handler page after they click the link in the email.
    url: `${window.location.origin}/auth/action`,
    handleCodeInApp: true,
  });
};

export const verifyResetCode = async (oobCode: string): Promise<string> => {
  return verifyPasswordResetCode(auth, oobCode);
};

export const resetPassword = async (
  oobCode: string,
  newPassword: string
): Promise<void> => {
  await confirmPasswordReset(auth, oobCode, newPassword);
};

export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};


export const checkEmailExists = async (email: string): Promise<boolean> => {
  const methods = await fetchSignInMethodsForEmail(auth, email);
  return methods.length > 0;
};

export const sendVerificationEmail = async (user: User): Promise<void> => {
  await sendEmailVerification(user);
};

export const changePassword = async (
  oldPassword: string,
  newPassword: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user?.email) {
    throw new Error('No user is currently logged in');
  }

  const credential = EmailAuthProvider.credential(user.email, oldPassword);
  await reauthenticateWithCredential(user, credential);

  await updatePassword(user, newPassword);
};

export const onAuthChange = (
  callback: (user: User | null) => void
) => {
  return onAuthStateChanged(auth, callback);
};

const authService = {
  login,
  register,
  logout,
  deleteAccount,
  refreshToken,
  forgotPassword,
  verifyResetCode,
  resetPassword,
  getCurrentUser,
  onAuthChange,
  checkEmailExists,
  sendVerificationEmail,
  changePassword,
};

export default authService;
